import { COUNTRIES } from '../GscTools/countries';

/**
 * The three segmentations this report offers, named by the Search Console dimension each one is.
 *
 * They are the dimension strings themselves rather than labels of our own, because every request,
 * every filter and every exported row has to name the dimension exactly as the API does. A row
 * returned under `country` and a row returned under `searchAppearance` are counts of different
 * partitions of the same traffic, and the only thing that keeps them apart is this key.
 */
export const SEGMENT = {
    COUNTRY: 'country',
    DEVICE: 'device',
    APPEARANCE: 'searchAppearance',
};

export const SEGMENTS = [SEGMENT.COUNTRY, SEGMENT.DEVICE, SEGMENT.APPEARANCE];

/**
 * Search Console cannot combine the searchAppearance dimension with any other dimension, so a
 * report keyed by appearance is read without the scope's country, device and page filters. It is
 * stated on the page rather than worked around: a table that quietly ignored half the scope bar
 * would be read as though it had honoured it.
 */
export function segmentTakesFilters(segment) {
    return segment !== SEGMENT.APPEARANCE;
}

const COUNTRY_NAMES = new Map(COUNTRIES.map((country) => [country.value, country.label]));

/**
 * The readable name for one returned segment key, or the key itself when there is no name for it.
 *
 * Only the display changes. The key Search Console returned is what every filter, export and
 * request keeps using — a country row relabelled "United States" still filters on `usa`, and an
 * appearance enum this app has never heard of stays displayable as its raw key rather than
 * disappearing behind an "unknown".
 */
export function segmentKeyLabel(t, segment, key) {
    if (!key) return '';

    if (segment === SEGMENT.COUNTRY) return COUNTRY_NAMES.get(key.toLowerCase()) ?? key;

    if (segment === SEGMENT.DEVICE) {
        const label = t(`tools.gsc.devices.${key.toLowerCase()}`);
        // i18next echoes the key back when it has no string for it; an unrecognised device enum
        // is shown as Search Console sent it instead of as a translation key.
        return label.includes('tools.gsc.devices') ? key : label;
    }

    return key;
}

/** The label of the segmentation itself, as the controls and the column heading name it. */
export function segmentLabel(t, segment) {
    return t(`tools.audience_appearance.segments.${segment}`);
}

/** RE2 metacharacters escaped, so a prefix is matched as the literal text it is. */
function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const MAX_PREFIX_LENGTH = 500;

/**
 * The anchored regular expression one page prefix becomes, or null when there is no prefix.
 *
 * Search Console's page values are absolute URLs, so a path typed on its own is anchored after
 * the origin rather than at the start of the string — `^/news/` would match nothing at all. A
 * prefix given as a full URL is anchored where it was written. Either way the user's text is
 * escaped first: it is a prefix somebody typed, not a pattern they wrote, and an unescaped dot
 * in a hostname would quietly match more pages than they asked for.
 */
export function pagePrefixRegex(prefix) {
    const value = (prefix ?? '').trim();
    if (!value) return null;

    if (/^https?:\/\//i.test(value)) return `^${escapeRegex(value)}`;

    const path = value.startsWith('/') ? value : `/${value}`;
    return `^https?://[^/]+${escapeRegex(path)}`;
}

/** Why a prefix cannot be used, or null when it can. */
export function pagePrefixError(prefix, t) {
    const value = (prefix ?? '').trim();
    if (!value) return null;

    if (value.length > MAX_PREFIX_LENGTH) {
        return t('tools.audience_appearance.errors.page_prefix_long', { max: MAX_PREFIX_LENGTH });
    }

    if (/\s/.test(value)) return t('tools.audience_appearance.errors.page_prefix_spaces');

    return null;
}

/** The page filter a resolved scope carries, as a list so it can be spread into a request. */
export function pageFilters(scope) {
    if (!scope?.pageRegex) return [];
    return [{ dimension: 'page', operator: 'includingRegex', expression: scope.pageRegex }];
}
